/**
 * # `plan_task_bounce` — Plan Task Bounce Tracking
 *
 * ## Purpose
 *
 * Tracks when team members click external links in training plan tasks,
 * bouncing them out of Pathway. Establishes a baseline bounce rate for
 * the upcoming "Plans: Embed Videos" feature (PATHWAY-3283).
 *
 * ## Event
 *
 * **`plan_task_bounce`** — fires once per click on a task link that points
 * outside Pathway. Internal links (same origin) are silently ignored.
 *
 * | Property            | Type   | Description                              |
 * | ------------------- | ------ | ---------------------------------------- |
 * | `url`               | string | Destination URL the user clicked         |
 * | `training_plan_id`  | string | Plan the task belongs to                 |
 * | `step_id`           | string | Step within the plan                     |
 * | `step_name`         | string | Localized step name at time of click     |
 *
 * Properties captured at the Identify level (on every event automatically):
 * `is_webview`, `user_type`, `permission_level`, `user_locations`
 *
 * ## Flow
 *
 * ```
 * PlanItemStepLink (click)
 *   → trackPlanTaskBounce()
 *     → isExternal? (url vs window.location.origin)
 *       ├─ yes → track('plan_task_bounce', { ... })
 *       └─ no  → noop
 * ```
 *
 * ## Analysis
 *
 * In Amplitude, segment on this event vs automatic page views to derive
 * bounce rate from plans. Once videos are embedded in-app, a drop in
 * `plan_task_bounce` volume directly measures reduced bounces.
 */
import { Track } from '@/amplitude/useAmplitude';

interface PlanTaskBounceParams {
  url: string;
  planId: string;
  stepId?: string;
  stepName?: string;
}

export const trackPlanTaskBounce = (
  track: Track | null,
  { url, planId, stepId, stepName }: PlanTaskBounceParams,
) => {
  if (!url) return;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url, window.location.origin);
  } catch {
    return;
  }

  const isExternal = parsedUrl.origin !== window.location.origin;
  if (!isExternal) return;

  track?.('plan_task_bounce', {
    url,
    training_plan_id: planId,
    step_id: stepId,
    step_name: stepName,
  });
};
